import Tasks from '../../tasks';
import Modal from '../../tasks/modal';
import BaseCell from '../base-cell';
import { fn } from '@ember/helper';
import { action } from '@ember/object';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import BvBtn from 'volta/components/bv-btn';
import can from 'volta/helpers/can';
import isPresent from 'volta/helpers/is-present';
import { formatLocalDate } from 'volta/utils/date-utils';

import type { CellBaseCellSignature } from 'volta/components/cell/base-cell';
import type Task from 'volta/models/task';
import type StoreService from 'volta/services/store';

export default class CellTaskCellComponent extends Component<CellBaseCellSignature> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    <BaseCell
      @cell={{@cell}}
      @row={{@row}}
      @column={{@column}}
      @scrollToRow={{@scrollToRow}}
      as |cell|
    >
      {{#if (can 'ViewTasks' cell.value.resourceName.plural)}}
        {{#let cell.value as |value|}}
          {{#if (isPresent value.isOpened)}}
            {{! template-lint-disable no-at-ember-render-modifiers }}
            <div {{didUpdate (fn this.update value.tasks value.dueDate) value.tasks}}>
              <BvBtn
                @text={{this.count}}
                @color={{this.criticality}}
                @subtle={{true}}
                @iconLeft='checkbox-outline'
                @size='sm'
                @onClick={{fn value.toggleTasks value.dueDate}}
              />
            </div>
            {{#if value.isOpened}}
              <Modal
                @inPage={{value.inPage}}
                @onDestroy={{value.onCloseTasks}}
                @onClose={{optional value.toggleTasks}}
                @onDeleteTask={{value.onDeleteTask}}
                @onDraftTask={{value.onDraftTask}}
                @onSaveTask={{value.onSaveTask}}
                @scrollToDate={{value.dueDate}}
                @entityId={{value.entityId}}
                @tasks={{value.tasks}}
                @taskType={{value.taskType}}
                @isAutoResolvable={{true}}
                @groupingField='dueDate'
                @entityResourceName={{value.resourceName}}
              />
            {{/if}}
          {{else}}
            <Tasks
              @minimized={{true}}
              @entityId={{value.entityId}}
              @taskType={{value.taskType}}
              @entityResourceName={{value.resourceName}}
              @aggStats={{value.aggStats}}
              @showDueDate={{value.showDueDate}}
            />
          {{/if}}
        {{/let}}
      {{/if}}
    </BaseCell>
  </template>
  @service store!: StoreService;

  @tracked scrollToDate?: string;
  @tracked tasks: Task[] = [];

  get count() {
    return this.tasksWithSameDueDate.length;
  }

  get criticality() {
    const ranks = ['success', undefined, 'warning', 'danger'];
    const bestCriticality = this.tasksWithSameDueDate.reduce((acc, task) => {
      const rank = ranks.indexOf(task.todoCriticality || undefined);
      if (rank > acc) {
        acc = rank;
      }
      return acc;
    }, 0);
    return [undefined, undefined, 'danger', 'danger'][bestCriticality] as 'hud' | 'danger';
  }

  get tasksWithSameDueDate() {
    if (this.scrollToDate) {
      const formattedDueDate = formatLocalDate(this.scrollToDate);
      return this.tasks.filter((task) => {
        if (task) {
          if (!task.dueDate || task.resolvedAt) {
            return false;
          }
          return formatLocalDate(task.dueDate) === formattedDueDate;
        } else {
          return false;
        }
      });
    }
    return [];
  }

  @action
  update(tasks: Task[], date: string) {
    this.tasks = tasks;
    this.scrollToDate = date;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Cell::TaskCell': typeof CellTaskCellComponent;
    'cell/task-cell': typeof CellTaskCellComponent;
  }
}
